import {
    Button,
    Checkbox,
    Grid,
    IconButton,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import {BillingPartyData, BookingData, GuestData} from '../helperFunctions';
import {getHasError} from '../../../utils/formHelpers';
import validate from 'validate.js';
import {InternationalPhoneInput} from '../../../components/PhoneInput/PhoneInput';
import {RegionType, sortedCountries} from '../../../utils/countries';
import BookingToolDialog from '../BookingToolDialog';
import ExpandableSection from '../ExpandableSection';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3),
        width: '100%',

        '& .MuiFormControl-fullWidth': {
            display: 'flex',
            marginTop: 25,
        },

        '& .MuiTextField-root label': {
            fontFamily: 'Nunito',
            fontWeight: 'bold',
            fontSize: 20
        },

        '& .MuiInput-root input, & .MuiSelect-select': {
            color: theme.palette.secondary.main,
            fontSize: 20,

            '&::placeholder': {
                color: theme.palette.black
            }
        },

        '& MuiPaper-root .MuiExpansionPanel-root': {
            marginTop: 0,
        },

        '& .MuiExpansionPanel-root.Mui-expanded': {
            margin: 0,
        },
    },
    constrainedWidth: {
        [theme.breakpoints.up('md')]: {
            maxWidth: '78%',
            marginLeft: 'auto',
            marginRight: 'auto',
        },
    },
    padBottom: {
        paddingBottom: theme.spacing(3),
    },
    subheading: {
        fontFamily: 'Nunito',
        fontWeight: 'bold',
        fontSize: 24,
    },
    body: {
        fontFamily: 'Nunito',
        fontSize: 18,
    },
    textField: {
        width: '100%',
    },
    phoneInputLabel: {
        display: 'block',
        marginTop: 25,
        transform: 'scale(0.75)',
        transformOrigin: 'bottom left'
    },
    phoneInput: {
        '& label': {
            display: 'none'
        }
    },
    removePartyContainer: {
        textAlign: 'right',
        marginTop: theme.spacing(2),
    },
    removePartyButton: {
        borderRadius: 0,
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
        textTransform: 'none',
        fontSize: 14,
        background: '#f2f2f2',

        '&:hover, &.Mui-focusVisible': {
            borderRadius: 0,
            borderWidth: 2,
        },

        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    billingPartyHeader: {
        fontSize: 18,
        marginTop: theme.spacing(3.5)
    },
    checkbox: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: -12,
        fontSize: 16,

        '& .MuiCheckbox-root': {
            color: theme.palette.secondary.main,
        },

        '& .MuiCheckbox-root.Mui-disabled': {
            color: '#B2B2B2',
        },
    },
    listCheckbox: {
        marginLeft: -9,
    },
    rightColumn: {
        textAlign: 'right',
    },
    guestSelectionContainer: {
        marginTop: theme.spacing(4),
    },
    guestList: {
        fontSize: 14,
        fontStyle: 'italic',
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
    warningText: {
        color: theme.palette.error.main,
        paddingTop: theme.spacing(1),
    }
}));

interface Props {
    partyData: BillingPartyData;
    bookingData: BookingData;
    addBillingParty: () => void;
    removeBillingParty: (partyData: BillingPartyData) => void;
    setBillingPartyData: (partyData: BillingPartyData) => void;
    setGuestBillingParty: (guestData: GuestData, billingId: string) => void;
    allGuestData: GuestData[];
    toggleRemainingGuestsParty: (billingPartyId: string, toggleOn: boolean) => void;
    billingPartyEmpty: boolean;
}

const BillingParty = (props: Props) => {
    const classes = useStyles();
    const {
        partyData,
        bookingData,
        removeBillingParty,
        setBillingPartyData,
        setGuestBillingParty,
        allGuestData,
        toggleRemainingGuestsParty,
        billingPartyEmpty
    } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
        defaultMatches: false
    });
    const [regions, setRegions] = useState<RegionType[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const billingPartyIndex = bookingData.billingPartiesData.indexOf(partyData);
    const unclaimedGuests = allGuestData.filter(guest => guest.billingPartyId === '');
    const billingPartyGuests = allGuestData.filter(guest => guest.billingPartyId === partyData.id);
    const otherBillingPartyGuests = allGuestData.filter(guest => guest.billingPartyId !== partyData.id && guest.billingPartyId !== '');
    const hasUnclaimedGuests = unclaimedGuests.length > 0;

    const handleFieldChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        let value: any = event.target.type === 'checkbox' ? (event.target as HTMLInputElement).checked : event.target.value;
        let newPartyData = {...partyData};

        switch (typeof (partyData as any)[event.target.name]) {
            case 'number':
                value = Number(value);
                break;
            case 'boolean':
                value = Boolean(value);
                break;
        }

        newPartyData = {...newPartyData, [event.target.name]: value}

        if (event.target.name === 'country') {
            const selectedCountry = sortedCountries.find(country => country.countryShortCode === value);
            if (selectedCountry) {
                newPartyData = {
                    ...newPartyData,
                    primary_phone_code: selectedCountry.phone,
                    secondary_phone_code: selectedCountry.phone,
                }
            } else {
                newPartyData = {
                    ...newPartyData,
                    primary_phone_code: '',
                    secondary_phone_code: '',
                }
            }
        }

        setBillingPartyData(newPartyData);
    };

    const handleGuestChecked = (guestData: GuestData) => {
        setGuestBillingParty(guestData, guestData.billingPartyId === '' ? partyData.id : '');
    };

    //2. Setup helper functions for form
    const [errors, setErrors] = useState({
        isValid: true,
        errors: {}
    });
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        first_name: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        last_name: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        email: {
            presence: {allowEmpty: false, message: 'is required'},
            email: true,
            length: {
                maximum: 64
            }
        },
        address_line_1: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        city: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        state: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        zipcode: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        country: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        primary_phone: {
            presence: {allowEmpty: false, message: 'is required'},
            phone: true,
        },
    };

    useEffect(() => {
        const newErrors = validate(partyData, validateSchema);
        const isValid = !newErrors;
        const selectedCountry = sortedCountries.find(country => country.countryShortCode === partyData.country);

        setErrors(state => ({
            ...state,
            isValid,
            errors: newErrors || {}
        }));

        if (selectedCountry) {
            if (regions !== selectedCountry.regions) {
                setRegions(selectedCountry.regions);
            }
        }

        if (partyData.isValid !== isValid) {
            setBillingPartyData({...partyData, isValid});
        }

    }, [partyData]);

    return (
        <Grid className={classes.root} container spacing={0}>
            <Grid item xs={12}>
                <Typography variant="h2" className={clsx([classes.subheading, classes.padBottom])}>
                    Billing Party {billingPartyIndex + 1}
                </Typography>
                <Typography variant="body1" className={clsx([classes.body, classes.padBottom])}>
                    Please enter the person(s) responsible for making payments below. If there are multiple billing parties on this reservation, please add their information by clicking the “+ Add Billing Party” box below.
                </Typography>
            </Grid>
            <Grid container className={classes.constrainedWidth}>
                {billingPartyEmpty &&
                    <Typography variant="body1" className={clsx([classes.body, classes.warningText])}>
                        Each Billing Party must be assigned at least 1 traveler.
                    </Typography>}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="First Name*"
                        name="first_name"
                        onChange={handleFieldChange}
                        {...hasError('first_name')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={partyData.first_name}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Last Name*"
                        name="last_name"
                        onChange={handleFieldChange}
                        {...hasError('last_name')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={partyData.last_name}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Email*"
                        name="email"
                        onChange={handleFieldChange}
                        {...hasError('email')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={partyData.email}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        {...hasError('country')}
                        fullWidth
                        label="Country*"
                        name="country"
                        onChange={handleFieldChange}
                        select
                        // eslint-disable-next-line react/jsx-sort-props
                        SelectProps={{native: true, displayEmpty: true}}
                        value={partyData.country}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    >
                        <option value="">Please Select</option>
                        {sortedCountries.map((country) => (
                            <option key={country.countryShortCode} value={country.countryShortCode}>
                                {country.countryName}
                            </option>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Billing Address Line 1*"
                        name="address_line_1"
                        onChange={handleFieldChange}
                        {...hasError('address_line_1')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={partyData.address_line_1}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Billing Address Line 2 (Optional)"
                        name="address_line_2"
                        onChange={handleFieldChange}
                        {...hasError('address_line_2')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={partyData.address_line_2}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="City/Town*"
                        name="city"
                        onChange={handleFieldChange}
                        {...hasError('city')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={partyData.city}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        {...hasError('state')}
                        fullWidth
                        label="State*"
                        name="state"
                        onChange={handleFieldChange}
                        select
                        // eslint-disable-next-line react/jsx-sort-props
                        SelectProps={{native: true, displayEmpty: true}}
                        value={partyData.state}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        disabled={regions.length === 0}
                    >
                        <option value="">Please Select</option>
                        {regions.map((region) => (
                            <option key={region.shortCode} value={region.shortCode}>
                                {region.name}
                            </option>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Zipcode*"
                        name="zipcode"
                        onChange={handleFieldChange}
                        {...hasError('zipcode')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        value={partyData.zipcode}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body1" component="label" className={classes.phoneInputLabel}>
                        Phone Number (Primary)*
                    </Typography>
                    <InternationalPhoneInput
                        className={clsx(classes.textField, classes.phoneInput)}
                        {...hasError('primary_phone')}
                        notched="true"
                        label={'Primary Phone'}
                        name="primary_phone"
                        type="text"
                        value={partyData.primary_phone}
                        codeValue={partyData.primary_phone_code!}
                        onChange={handleFieldChange}
                        onCodeChange={handleFieldChange}
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body1" component="label" className={classes.phoneInputLabel}>
                        Phone Number (Secondary)
                    </Typography>
                    <InternationalPhoneInput
                        className={clsx(classes.textField, classes.phoneInput)}
                        {...hasError('secondary_phone')}
                        notched="true"
                        label={'Secondary Phone'}
                        name="secondary_phone"
                        type="text"
                        value={partyData.secondary_phone}
                        codeValue={partyData.secondary_phone_code!}
                        onChange={handleFieldChange}
                        onCodeChange={handleFieldChange}
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h3" className={classes.billingPartyHeader}>
                        Billing Party {billingPartyIndex + 1} includes
                    </Typography>
                    {billingPartyGuests.length > 0 && (
                        <Typography variant="body1" className={clsx(classes.body, classes.guestList)}>
                            {billingPartyGuests.map(guest => `${guest.first_name} ${guest.last_name}`).join(', ')}
                        </Typography>
                    )}
                    <Typography variant="body1" className={classes.body}>
                      Please select all travelers in this billing party. If there are multiple billing parties on this reservation, please add their information by clicking the “+ Add Billing Party” box below.
                    </Typography>
                </Grid>
                <Grid container item xs={12} justify="space-between" className={classes.guestSelectionContainer}>
                    <Grid item xs={6}>
                        <Typography
                            component="label"
                            variant="body1"
                            className={clsx(classes.body, classes.checkbox)}
                        >
                            <Checkbox
                                onChange={() => {
                                    toggleRemainingGuestsParty(partyData.id, hasUnclaimedGuests);
                                    // setHasFilledRemaining(hasUnclaimedGuests);
                                }}
                                checked={billingPartyGuests.length === allGuestData.length}
                            disabled={billingPartyIndex !== 0 || bookingData.billingPartiesData.length > 1 || otherBillingPartyGuests.length > 0}
                            />All Travelers
                        </Typography>
                    </Grid>

                    <Grid item xs={6} className={classes.rightColumn}>
                        <IconButton onClick={() => setDialogOpen(!dialogOpen)}>
                            {!isMobile && dialogOpen ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Grid>

                    {isMobile && (
                        <BookingToolDialog
                            open={dialogOpen}
                            onClose={() => setDialogOpen(false)}
                            title={`Billing Party ${billingPartyIndex + 1}`}
                        >
                            {allGuestData.map((guestData, index) => getListItem(guestData, index, classes, handleGuestChecked, partyData.id))}
                        </BookingToolDialog>
                    )}

                    {!isMobile && (
                        <ExpandableSection open={dialogOpen}>
                            {allGuestData.map((guestData, index) => getListItem(guestData, index, classes, handleGuestChecked, partyData.id))}
                        </ExpandableSection>
                    )}
                </Grid>
                <Grid item xs={12} className={classes.removePartyContainer}>
                    {billingPartyIndex > 0 && (
                        <Button
                            variant="outlined"
                            size="large"
                            color="primary"
                            className={classes.removePartyButton}
                            onClick={() => removeBillingParty(partyData)}
                        >
                            Remove billing party?
                        </Button>
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};

function getListItem(
    guestData: GuestData,
    index: number,
    classes: Record<string, any>,
    handleGuestChecked: (guestData: GuestData) => void,
    billingPartyId: string
): React.ReactNode {
    return (
        <Grid key={index} item xs={12}>
            <Typography
                component="label"
                variant="body1"
                className={clsx(classes.body, classes.checkbox, classes.listCheckbox)}
            >
                <Checkbox
                    onChange={() => handleGuestChecked(guestData)}
                    checked={guestData.billingPartyId !== ''}
                    disabled={guestData.billingPartyId !== '' && guestData.billingPartyId !== billingPartyId}
                />{guestData.first_name} {guestData.last_name}
            </Typography>
        </Grid>
    );
}

export default BillingParty;
